import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './ui/Icon';

export interface ConfirmCardField {
  label: string;
  value: string;
  emphasis?: 'primary' | 'secondary' | 'warning';
}

export interface ConfirmCardData {
  title: string;
  fields: ConfirmCardField[];
  onConfirm: () => void;
  onEdit?: () => void;
  onCancel: () => void;
}

interface VoiceConfirmCardProps {
  data: ConfirmCardData;
  variant?: 'overlay' | 'inline';
}

const VoiceConfirmCard: React.FC<VoiceConfirmCardProps> = ({ data, variant = 'overlay' }) => {
  const { t } = useTranslation();
  const wrapperClass = variant === 'inline' ? 'gva-confirm-inline' : 'gva-confirm-overlay';
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Preserve keyboard flow: move focus to the primary confirm action when the card appears.
    primaryRef.current?.focus();
  }, []);

  return (
    <div className={wrapperClass} role="region" aria-label={t('voice.confirm.region', 'Confirm action')}>
      <div className="gva-confirm-card">
        <h3 className="gva-confirm-title">{data.title}</h3>
        <div className="gva-confirm-divider" />
        <div className="gva-confirm-fields">
          {data.fields.map((field) => (
            <div
              key={`${field.label}-${field.value}`}
              className={`gva-confirm-row gva-confirm-row-${field.emphasis || 'primary'}`}
            >
              <span className="gva-confirm-label">{field.label}</span>
              <span className="gva-confirm-value">{field.value}</span>
            </div>
          ))}
        </div>
        <div className="gva-confirm-divider" />
        <div className="gva-confirm-actions">
          <button
            ref={primaryRef}
            type="button"
            className="gva-confirm-btn gva-confirm-btn-primary"
            onClick={data.onConfirm}
          >
            <Icon name="check" size={16} /> {t('voice.confirm.looksGood', 'Looks good')}
          </button>
          {data.onEdit && (
            <button type="button" className="gva-confirm-btn gva-confirm-btn-secondary" onClick={data.onEdit}>
              <Icon name="edit" size={16} /> {t('voice.confirm.editDetails', 'Edit details')}
            </button>
          )}
          <button type="button" className="gva-confirm-btn gva-confirm-btn-ghost" onClick={data.onCancel}>
            <Icon name="close" size={16} /> {t('voice.confirm.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoiceConfirmCard;
